import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LayoutDashboard, Users, Calendar, User, MapPin, Stethoscope, DollarSign,
  History as HistoryIcon, FileText, LogOut, Loader2, Wallet, Bell, Ticket,
} from 'lucide-react';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import ProfessionalProfilePage from './professional/ProfessionalProfilePage';
import ProfessionalVitalPassOffersPage from './professional/ProfessionalVitalPassOffersPage';

const MENU = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'clients', label: 'Clientes', icon: Users },
  { id: 'agenda', label: 'Agenda', icon: Calendar },
  { id: 'profile', label: 'Meu Perfil', icon: User },
  { id: 'vitalpass-offers', label: 'Ofertas VITALPASS', icon: Ticket },
  { id: 'locations', label: 'Meus Locais', icon: MapPin },
  { id: 'specialties', label: 'Especialidades', icon: Stethoscope },
  { id: 'receivables', label: 'Valores a Receber', icon: DollarSign },
  { id: 'payouts-history', label: 'Histórico de Repasses', icon: HistoryIcon },
  { id: 'terms', label: 'Termos/LGPD', icon: FileText },
];

const currency = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const ProfessionalDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [tab, setTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [requests, setRequests] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [me, dash, reqs] = await Promise.all([
          axios.get(`${API}/professional/me`),
          axios.get(`${API}/professional/dashboard`),
          axios.get(`${API}/professional/requests`),
        ]);
        setProfile(me.data);
        setDashboard(dash.data);
        setRequests(reqs.data.requests || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erro ao carregar painel');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/', { replace: true });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="professional-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 hidden md:flex flex-col">
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg overflow-hidden bg-gradient-to-br from-blue-600 to-slate-900 flex items-center justify-center">
              {profile?.photo_base64 ? (
                <img src={profile.photo_base64} alt="" className="w-full h-full object-cover" />
              ) : (
                <Stethoscope className="w-5 h-5 text-white" />
              )}
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {profile?.name || 'Profissional'}
              </p>
              <p className="text-[10px] uppercase tracking-wide text-teal-600 font-semibold">Profissional</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {MENU.map((item) => (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              data-testid={`prodash-menu-${item.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                tab === item.id ? 'bg-teal-50 text-teal-700 font-semibold' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100">
          <Button variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50" onClick={handleLogout} data-testid="prodash-logout">
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1">
        {/* Top header (visible on all tabs) */}
        <header className="bg-white border-b border-slate-200 px-4 md:px-8 py-3 flex items-center justify-between sticky top-0 z-30">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-400">
              {MENU.find((m) => m.id === tab)?.label || 'Dashboard'}
            </p>
            <p className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Olá, {profile?.name?.split(' ')[0] || 'Profissional'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setNotifOpen((v) => !v)}
                data-testid="prodash-notifications-btn"
                className="relative border-slate-200 h-9 w-9 p-0"
                aria-label="Notificações"
              >
                <Bell className="w-4 h-4 text-slate-600" />
                {requests.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-teal-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                    {Math.min(requests.length, 9)}
                  </span>
                )}
              </Button>
              {notifOpen && (
                <div
                  className="absolute right-0 top-11 w-72 bg-white border border-slate-200 rounded-xl shadow-lg z-50 overflow-hidden"
                  data-testid="prodash-notifications-panel"
                >
                  <div className="px-4 py-2 border-b border-slate-100 bg-slate-50">
                    <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">Notificações</p>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {requests.length === 0 ? (
                      <p className="px-4 py-6 text-xs text-slate-500 text-center">Nenhuma notificação nova</p>
                    ) : (
                      requests.slice(0, 8).map((r) => (
                        <div key={r.request_id} className="px-4 py-2 border-b border-slate-50 hover:bg-slate-50">
                          <p className="text-xs font-semibold text-slate-800">
                            Nova solicitação de <strong>{r.student_name}</strong>
                          </p>
                          <p className="text-[10px] text-slate-500">
                            {new Date(r.requested_at).toLocaleString('pt-BR')} • {currency(r.payout_amount)}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="prodash-logout-header"
              className="border-red-200 text-red-600 hover:bg-red-50 h-9"
            >
              <LogOut className="w-4 h-4 mr-1" /> Sair
            </Button>
          </div>
        </header>

        <div className="p-4 md:p-8">
        {tab === 'dashboard' && (
          <div>
            <h1 className="text-2xl font-black text-slate-900 mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Dashboard
            </h1>

            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card className="p-4 border-l-4 border-teal-500" data-testid="kpi-scheduled">
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Clientes Agendados</p>
                <p className="text-3xl font-black text-slate-900">{dashboard?.kpis?.clients_scheduled ?? 0}</p>
              </Card>
              <Card className="p-4 border-l-4 border-blue-500" data-testid="kpi-month">
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Clientes no Mês</p>
                <p className="text-3xl font-black text-slate-900">{dashboard?.kpis?.clients_this_month ?? 0}</p>
              </Card>
              <Card className="p-4 border-l-4 border-emerald-500" data-testid="kpi-total">
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Total Clientes</p>
                <p className="text-3xl font-black text-slate-900">{dashboard?.kpis?.total_clients ?? 0}</p>
              </Card>
            </div>

            {/* Financial breakdown */}
            <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
              <Wallet className="w-5 h-5 text-teal-600" /> Valores a Receber
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <Card className="p-3" data-testid="fin-pending">
                <p className="text-[10px] uppercase text-slate-500 font-semibold">Pendente</p>
                <p className="text-lg font-black text-amber-600">{currency(dashboard?.financials?.pending)}</p>
              </Card>
              <Card className="p-3" data-testid="fin-approved">
                <p className="text-[10px] uppercase text-slate-500 font-semibold">Aprovado</p>
                <p className="text-lg font-black text-blue-600">{currency(dashboard?.financials?.approved)}</p>
              </Card>
              <Card className="p-3" data-testid="fin-topay">
                <p className="text-[10px] uppercase text-slate-500 font-semibold">A Pagar</p>
                <p className="text-lg font-black text-purple-600">{currency(dashboard?.financials?.to_pay)}</p>
              </Card>
              <Card className="p-3" data-testid="fin-paid">
                <p className="text-[10px] uppercase text-slate-500 font-semibold">Pago</p>
                <p className="text-lg font-black text-emerald-600">{currency(dashboard?.financials?.paid)}</p>
              </Card>
            </div>
            <div className="mb-8 text-xs text-slate-500 bg-slate-100 border border-slate-200 rounded-lg p-3">
              💰 Cada VITALPASS consumido gera repasse fixo de <strong>{currency(dashboard?.financials?.token_value)}</strong> ao profissional.
            </div>
          </div>
        )}

        {tab === 'clients' && (
          <div>
            <h1 className="text-2xl font-black text-slate-900 mb-6">Clientes</h1>
            <Card className="p-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-slate-200">
                    <th className="py-2">Cliente</th>
                    <th>Data</th>
                    <th>Categoria</th>
                    <th>Status</th>
                    <th className="text-right">Repasse</th>
                  </tr>
                </thead>
                <tbody data-testid="clients-table-body">
                  {requests.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-6 text-slate-500">Nenhum cliente ainda</td></tr>
                  ) : requests.map((r) => (
                    <tr key={r.request_id} className="border-b border-slate-100">
                      <td className="py-2 font-semibold">{r.student_name || '-'}</td>
                      <td className="text-xs text-slate-500">{new Date(r.requested_at).toLocaleString('pt-BR')}</td>
                      <td className="text-xs">{(r.professional_category_ids || []).join(', ')}</td>
                      <td>
                        <span className="text-[10px] uppercase bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                          {r.payout_status || 'pending'}
                        </span>
                      </td>
                      <td className="text-right font-bold text-teal-700">{currency(r.payout_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
        )}

        {tab === 'profile' && (
          <ProfessionalProfilePage />
        )}

        {tab === 'vitalpass-offers' && (
          <ProfessionalVitalPassOffersPage />
        )}

        {tab === 'locations' && profile && (
          <div>
            <h1 className="text-2xl font-black text-slate-900 mb-6">Meus Locais de Atendimento</h1>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(profile.locations || []).map((l, i) => (
                <Card key={i} className="p-4">
                  <p className="text-[10px] uppercase text-slate-500 font-semibold mb-1">LOCAL {i + 1}</p>
                  {l.place_name && <p className="text-sm font-bold text-teal-700 mb-1">{l.place_name}</p>}
                  <p className="font-bold">{l.address}</p>
                  <p className="text-sm text-slate-600">{l.city} / {l.state}</p>
                  <p className="text-sm text-slate-600 mt-1">📞 {l.phone}</p>
                </Card>
              ))}
            </div>
          </div>
        )}

        {tab === 'specialties' && profile && (
          <div>
            <h1 className="text-2xl font-black text-slate-900 mb-6">Especialidades</h1>
            <Card className="p-4">
              <p className="text-xs uppercase text-slate-500 font-semibold mb-2">Categorias</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {(profile.categories || []).map((c) => (
                  <span key={c} className="bg-teal-100 text-teal-800 text-xs font-semibold px-3 py-1 rounded-full">{c}</span>
                ))}
              </div>
              <p className="text-xs uppercase text-slate-500 font-semibold mb-2">Especialidades</p>
              <div className="flex flex-wrap gap-2">
                {(profile.specialties || []).length === 0 ? <span className="text-sm text-slate-500">Nenhuma cadastrada</span> :
                  profile.specialties.map((s) => (
                    <span key={s} className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">{s}</span>
                  ))}
              </div>
            </Card>
          </div>
        )}

        {(tab === 'receivables' || tab === 'payouts-history') && (
          <div>
            <h1 className="text-2xl font-black text-slate-900 mb-6">
              {tab === 'receivables' ? 'Valores a Receber' : 'Histórico de Repasses'}
            </h1>
            <Card className="p-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-slate-200">
                    <th className="py-2">Data</th>
                    <th>Cliente</th>
                    <th>Status</th>
                    <th className="text-right">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.filter((r) => tab === 'receivables' ? r.payout_status !== 'paid' : true).map((r) => (
                    <tr key={r.request_id} className="border-b border-slate-100">
                      <td className="py-2 text-xs">{new Date(r.requested_at).toLocaleDateString('pt-BR')}</td>
                      <td className="text-sm">{r.student_name}</td>
                      <td>
                        <span className="text-[10px] uppercase bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                          {r.payout_status}
                        </span>
                      </td>
                      <td className="text-right font-bold text-teal-700">{currency(r.payout_amount)}</td>
                    </tr>
                  ))}
                  {requests.length === 0 && (
                    <tr><td colSpan={4} className="text-center py-6 text-slate-500">Nenhum registro ainda</td></tr>
                  )}
                </tbody>
              </table>
            </Card>
          </div>
        )}

        {(tab === 'agenda' || tab === 'terms') && (
          <div>
            <h1 className="text-2xl font-black text-slate-900 mb-6">{MENU.find((m) => m.id === tab)?.label}</h1>
            <Card className="p-8 text-center text-slate-500">
              Em breve. Nenhuma ação disponível ainda.
            </Card>
          </div>
        )}
        </div>
      </main>
    </div>
  );
};

export default ProfessionalDashboard;
